import {
  EmployeeInvitationStatusTypes,
  SubscriberProgress,
  SubscriberIMQProgress,
  SubscribersFilter,
} from 'types/electionService';
import { closeQuotingTasks, getSubscribersPageForGroup, inviteAll } from 'api/operations/electionService';
import { applyProposalUserAction } from 'api/operations/enrollmentService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import { USER_ACTIONS } from 'types/enrollmentService';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import ModalDelegate from 'store/general/modalDelegate';
import { ExpectedGroupSizeModalStore } from 'store/pages/quote/add-employees/expectedGroupSizeModalStore';
import { EditExpectedGroupSizeModalStore } from 'store/pages/quote/add-employees/editExpectedGroupSizeModalStore';
import { redirectToRoute } from 'routes/redirectToRoute';
import { CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getRootStoreInstance } from 'store/rootStore';
import { imqAddEmployeesEmployeeActions } from './imqEmployeeActions';

interface IMQAddEmployeesFilters extends FiltersObject {
  status: EmployeeInvitationStatusTypes | null;
  subProgress: SubscriberIMQProgress | null;
  employeeName: string;
}

export default class IMQAddEmployeesPageStore extends IAddEmployeesPage<IMQAddEmployeesFilters> {
  protected _domainParams: QuoteFlowDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;
  public readonly expectedGroupSizeModalStore: ExpectedGroupSizeModalStore;
  public readonly editExpectedGroupSizeModalStore: EditExpectedGroupSizeModalStore;
  public inviteAllModalDelegate = new ModalDelegate();
  private _rootStore = getRootStoreInstance();

  public get stepCanComplete(): boolean {
    return (
      this.employees.length > 0 &&
      this.employees.every((employee) => employee.rawData.progress === SubscriberProgress.COMPLETED)
    );
  }

  public get showExpectedGroupSize(): boolean {
    return this._rootStore.domainStoreV2.quotingEstimatedGroupSize;
  }

  public get expectedGroupSize(): string {
    return this.flowDelegate.cartModule.lastUpdateBrowsingCriteria.groupSize || '';
  }

  public async getInitialData(): Promise<void> {
    const { cartId } = this._domainParams;
    this.flowDelegate.setIsInitialDataLoading(true);

    await Promise.all([this.flowDelegate.getFlowInitialData(), this.flowDelegate.cartModule.getCart(cartId)]);

    runInAction(() => {
      const { groupSize } = this.flowDelegate.cartModule.lastUpdateBrowsingCriteria;
      if (this.showExpectedGroupSize && !groupSize) {
        this.expectedGroupSizeModalStore.open();
      }
      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscribersPageForGroup({
      groupId: this._domainParams.groupId,
      cartId: this._domainParams.cartId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: this.paginationDelegate.filters.status ? [this.paginationDelegate.filters.status] : [],
      addOnMethod: [],
      progress: this.paginationDelegate.filters.subProgress ? [this.paginationDelegate.filters.subProgress] : [],
      filter: SubscribersFilter.ALL,
      page: this.paginationDelegate.page,
      name: this.paginationDelegate.filters.employeeName,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    this._setIsLoading(true);
    await closeQuotingTasks(this._domainParams.groupId);
    await applyProposalUserAction(
      this._domainParams.periodId,
      this._domainParams.cartId,
      USER_ACTIONS.COMPLETE_ADD_EMPLOYEES,
    );
    runInAction(() => {
      this._setIsLoading(false);
      this.flowDelegate.onCompleteRedirect();
      toastManager.addToast(
        `IMQ files have been generated and are available on the Carrier's portal.`,
        ToastColorTypes.SUCCESS,
        {
          persistFor: 10000, // 10 seconds
        },
      );
    });
  }

  public async inviteAllEmployees(): Promise<void> {
    this._setIsLoading(true);
    const success = await inviteAll(this._domainParams.groupId);
    if (success) {
      await this.getEmployees();
      toastManager.addToast('Invitations sent successfully!', ToastColorTypes.SUCCESS);
    }
    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public onBackRedirect(): void {
    redirectToRoute(QuoteRouteV2Id.QUOTE_CHECKLIST, {
      groupId: this._domainParams.groupId,
      periodId: this._domainParams.periodId,
      cartId: this._domainParams.cartId,
    });
  }

  constructor(params: QuoteFlowDomainParams) {
    super(
      { status: null, employeeName: '', subProgress: null },
      getStandardActionsWithSelfEnrollFeature(imqAddEmployeesEmployeeActions),
      params,
    );
    this._domainParams = params;

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });
    const refreshCart = (cartId: string): Promise<void> => {
      const cartModule = runInAction(() => this.flowDelegate.cartModule);
      return cartModule.getCart(cartId);
    };

    this.expectedGroupSizeModalStore = new ExpectedGroupSizeModalStore({
      getCartId: (): string => this._domainParams.cartId,
      getBrowsingCriteria: (): CartBrowsingCriteria => this.flowDelegate.cartModule.lastUpdateBrowsingCriteria,
      onBackRedirect: (): void => this.onBackRedirect(),
      refreshCart,
    });
    this.editExpectedGroupSizeModalStore = new EditExpectedGroupSizeModalStore({
      getCartId: (): string => this._domainParams.cartId,
      getBrowsingCriteria: (): CartBrowsingCriteria => this.flowDelegate.cartModule.lastUpdateBrowsingCriteria,
      refreshCart,
    });

    makeObservable<IMQAddEmployeesPageStore, '_domainParams'>(this, {
      _domainParams: observable,
      inviteAllModalDelegate: observable,
      expectedGroupSizeModalStore: observable,
      editExpectedGroupSizeModalStore: observable,
      stepCanComplete: computed,
      showExpectedGroupSize: computed,
      expectedGroupSize: computed,
      getInitialData: action.bound,
      getEmployees: action.bound,
      onComplete: action.bound,
      inviteAllEmployees: action.bound,
      onBackRedirect: action.bound,
    });
  }
}

const IMQAddEmployeesPageStoreContext = createContext(
  new IMQAddEmployeesPageStore({
    groupId: 'stub-group-id',
    cartId: 'stub-cart-id',
    periodId: 'stub-enrollment-id',
  }),
);

export const useIMQAddEmployeesPageStore = (): IMQAddEmployeesPageStore => {
  return useContext(IMQAddEmployeesPageStoreContext);
};

export { IMQAddEmployeesPageStore, IMQAddEmployeesPageStoreContext };
